/*
 * Copyright (c) 2026. AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

export type SafeErrorCategory = "network" | "server" | "client" | "cancelled" | "decode" | "unknown";

/**
 * Allowlisted, non-sensitive description of a failure. Raw Axios errors carry the
 * request config (submitted body, URL, headers) and response payload, so they must
 * never be passed to console directly. Only these three fields are ever emitted.
 */
export interface SafeErrorSummary {
  category: SafeErrorCategory;
  status: number | null;
  service_error_code: number | null;
}

const isNumber = (value: unknown): value is number => typeof value === "number" && isFinite(value);

interface AxiosLikeError {
  config?: unknown;
  response?: { status?: unknown; data?: unknown };
  __CANCEL__?: boolean;
}

export function summarizeError(error: unknown): SafeErrorSummary {
  const summary: SafeErrorSummary = { category: "unknown", status: null, service_error_code: null };
  if (!error || typeof error !== "object") return summary;

  try {
    const candidate = error as AxiosLikeError;
    if (candidate.__CANCEL__ === true) {
      summary.category = "cancelled";
      return summary;
    }

    if (!!candidate.config) {
      const response = candidate.response;
      const status = response ? response.status : null;
      summary.status = isNumber(status) ? status : null;
      if (!response) {
        summary.category = "network";
      } else if (summary.status !== null && summary.status >= 500) {
        summary.category = "server";
      } else if (summary.status !== null && summary.status >= 400) {
        summary.category = "client";
      }
      const data = response && response.data;
      const serviceErrorCode = data && typeof data === "object" ? (data as { errorCode?: unknown }).errorCode : null;
      summary.service_error_code = isNumber(serviceErrorCode) ? serviceErrorCode : null;
      return summary;
    }

    if (error instanceof Error) {
      const constructorName = error.constructor && (error.constructor as { name?: string }).name;
      if (/DecodeError$/.test(constructorName || "")) {
        summary.category = "decode";
      }
    }
  } catch (e) {
    // Summarizing must never throw; fall through with the unknown summary.
  }
  return summary;
}

/**
 * Logs a failure using only allowlisted fields. `context` must be a fixed literal
 * describing the call site, never user or server supplied text.
 */
export function logSafeError(context: string, error: unknown): void {
  try {
    console.error(`[auth.error] ${context}`, summarizeError(error));
  } catch (e) {
    // Diagnostics are best effort.
  }
}
